import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { signInFailureText } from '../utils/loadFailure';
import { signInReason } from '../utils/signInReason';

// Email/password login. On success, return to the page the user came from
// (location.state.from — the same convention as the list↔detail contract),
// falling back to home. The API deliberately returns one generic 401 for
// every login failure (no account enumeration) — surface it as one message.
const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err.response?.status === 401
          ? 'Invalid email or password.'
          : (signInFailureText(err) || 'Something went wrong. Please try again.')
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Log in — The Dragon's Table</title>
        <meta name="description" content="Log in to The Dragon's Table to save your favorite spells, feats, monsters, and more." />
      </Helmet>

      <div className="max-w-sm mx-auto space-y-6">
        <div className="card">
          <h1 className="section-header flex items-center gap-2">
            <LogIn className="w-5 h-5" />
            <span>Log in</span>
          </h1>

          {signInReason(from) && (
            <p className="text-sm mt-3" role="note" data-testid="sign-in-reason" style={{ color: 'var(--text-secondary)' }}>
              {signInReason(from)}
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4 mt-4" noValidate>
            <div>
              <label htmlFor="login-email" className="form-label">Email</label>
              <input
                id="login-email"
                type="email"
                className="input"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="login-password" className="form-label">Password</label>
              <input
                id="login-password"
                type="password"
                className="input"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            {error && <p className="form-error" role="alert">{error}</p>}

            <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
              {submitting ? 'Logging in…' : 'Log in'}
            </button>
          </form>

          <div className="mt-6 pt-4 space-y-3" style={{ borderTop: '1px solid var(--border)' }}>
            <GoogleSignInButton
              onSuccess={() => navigate(from, { replace: true })}
              onError={(message) => setError(message)}
            />
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              No account yet?{' '}
              <Link to="/register" state={{ from }} className="footer-link" style={{ color: 'var(--link)' }}>
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
